import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { AppContext } from '../context/AppContext';
import { Link } from 'react-router-dom';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    Title,
    Tooltip,
    Legend,
    Filler,
    ArcElement
} from 'chart.js';

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Title, Tooltip, Legend, Filler, ArcElement);

const BusinessAnalysis = () => {
    const { t } = useTranslation();
    const { authFetch } = useContext(AppContext);
    
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchAnalysis();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchAnalysis = async () => {
        try {
            setLoading(true);
            const res = await authFetch('/api/business_analysis');
            if (res.ok) {
                const json = await res.json();
                setData(json);
            }
        } catch (err) {
            console.error("Analysis Fetch Error:", err);
        } finally {
            setLoading(false);
        }
    };

    const formatCurrency = (val) => {
        const formatted = new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(val || 0);
        return `${formatted} CFA`;
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center min-h-[60vh] space-y-3">
                <div className="w-10 h-10 border-3 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
                <p className="text-gray-500 font-medium text-sm">{t('Loading business performance data...')}</p>
            </div>
        );
    }

    if (!data) return <div className="p-8 text-center text-red-600 font-medium">Failed to load performance metrics.</div>;

    const summary = data.summary;
    const perf = data.performance;
    const valuation = data.valuation;
    const loss = data.loss_analysis;
    const be = data.break_even;
    const salesFeed = data.today_sales_feed || [];

    return (
        <div className="space-y-6 max-w-7xl mx-auto pb-16">
            {/* Header Section */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 bg-white p-6 rounded-xl border border-gray-200 shadow-sm">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">
                        {t('Business Performance & Analysis')}
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">
                        {t('Financial health, inventory valuation, and profitability metrics.')}
                    </p>
                </div>
                <div className="flex items-center gap-3">
                    <Link to="/sales-reports" className="bg-white px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition shadow-sm flex items-center gap-2">
                        📊 {t('Sales Management')}
                    </Link>
                    <div className="bg-emerald-50 text-emerald-700 px-3 py-1.5 rounded-lg border border-emerald-200 flex items-center gap-2 text-xs font-semibold">
                        <span className="w-2 h-2 bg-emerald-500 rounded-full"></span>
                        {t('Real-Time Data')}
                    </div>
                </div>
            </div>

            {/* Smart Summary Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <SummaryCard 
                    title={t('Total Investment')} 
                    value={formatCurrency(summary.total_investment)} 
                    subtitle={t('Historical capitalized cost')}
                    icon="💰"
                />
                <SummaryCard 
                    title={t('Total Revenue')} 
                    value={formatCurrency(summary.total_revenue)} 
                    subtitle={t('Gross lifetime inflows')}
                    icon="📈"
                    trend="+12%" 
                    trendUp={true}
                />
                <SummaryCard 
                    title={t('Actual Net Profit')} 
                    value={formatCurrency(summary.net_profit)} 
                    subtitle={t('Net earnings to date')}
                    icon="💎"
                />
                <SummaryCard 
                    title={t('Profit Today')} 
                    value={formatCurrency(perf.today.profit)} 
                    subtitle={t('Daily performance delta')}
                    icon="⚡"
                    trend={`${perf.performance_vs_avg.toFixed(1)}%`}
                    trendUp={perf.performance_vs_avg >= 0}
                    highlight={true}
                />
            </div>

            {/* Periodic Profit Window */}
            <div className="bg-gray-900 rounded-xl p-6 text-white border border-gray-800 shadow-sm">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 divide-y md:divide-y-0 md:divide-x divide-gray-800">
                    <div className="pt-4 md:pt-0 md:px-4">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{t('Today Profit')}</p>
                        <p className="text-3xl font-bold">{formatCurrency(perf.today.profit)}</p>
                        <p className="text-xs text-gray-400 mt-2 font-medium">{t('Revenue')}: {formatCurrency(perf.today.revenue)}</p>
                    </div>
                    <div className="pt-4 md:pt-0 md:px-6">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{t('Monthly Profit')}</p>
                        <p className="text-3xl font-bold">{formatCurrency(perf.month.profit)}</p>
                        <p className="text-xs text-gray-400 mt-2 font-medium">{t('MTD Revenue')}: {formatCurrency(perf.month.revenue)}</p>
                    </div>
                    <div className="pt-4 md:pt-0 md:px-6">
                        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1">{t('Yearly Profit')}</p>
                        <p className="text-3xl font-bold">{formatCurrency(perf.year.profit)}</p>
                        <p className="text-xs text-gray-400 mt-2 font-medium">{t('YTD Revenue')}: {formatCurrency(perf.year.revenue)}</p>
                    </div>
                </div>
            </div>

            {/* Warehouse Valuation Window */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div className="lg:col-span-2 space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
                        <div className="p-5 border-b border-gray-100 bg-gray-50/70">
                            <h3 className="text-base font-bold text-gray-800">{t('Warehouse Valuation Profile')}</h3>
                            <p className="text-xs text-gray-500 font-medium">{t('Audit of total inventory value across branches.')}</p>
                        </div>
                        <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('Stock Purchase Cost')}</p>
                                <p className="text-2xl font-bold text-gray-900">{formatCurrency(valuation.inventory_cost)}</p>
                                <p className="text-xs text-gray-500 mt-1">{t('Capital in stock')}</p>
                            </div>
                            <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('Valuation after TVA')}</p>
                                <p className="text-2xl font-bold text-gray-900">{formatCurrency(valuation.inventory_selling_value)}</p>
                                <p className="text-xs text-emerald-600 font-medium mt-1">{t('Gross Recovery Potential')}</p>
                            </div>
                            <div className="p-4 bg-indigo-50/70 border border-indigo-100 rounded-lg">
                                <p className="text-xs font-semibold text-indigo-700 uppercase tracking-wider mb-1">{t('Projected Gain')}</p>
                                <p className="text-2xl font-bold text-indigo-700">{formatCurrency(valuation.potential_inventory_profit)}</p>
                                <p className="text-xs text-indigo-600 font-medium mt-1">{t('Estimated Margin')}</p>
                            </div>
                        </div>

                        {/* Liquidation Gauge */}
                        <div className="p-6 pt-0">
                            <div className="bg-gray-900 rounded-lg p-5 text-white">
                                <div className="flex justify-between items-center mb-3">
                                    <h4 className="text-xs font-semibold uppercase tracking-wider text-gray-400">{t('Liquidation Progress')}</h4>
                                    <span className="px-2.5 py-0.5 bg-indigo-900/60 text-indigo-300 rounded text-xs font-semibold">
                                        {be.progress_pct.toFixed(2)}% {t('Recovered')}
                                    </span>
                                </div>
                                <p className="text-2xl font-bold mb-3">{formatCurrency(be.current)} <span className="text-sm font-normal text-gray-400">/ {formatCurrency(be.target)}</span></p>
                                <div className="relative h-2.5 bg-gray-800 rounded-full overflow-hidden">
                                    <div 
                                        className="absolute top-0 left-0 h-full bg-emerald-500 rounded-full"
                                        style={{ width: `${Math.min(100, be.progress_pct)}%` }}
                                    ></div>
                                </div>
                                <p className="text-xs text-gray-400 mt-3">
                                    {t('Cumulative revenue compared against total inventory cost target.')}
                                </p>
                            </div>
                        </div>
                    </div>

                    {/* Daily Sales Feed Section */}
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-base font-bold text-gray-800">{t('Today\'s Transactions')}</h3>
                            <Link to="/sales-reports" className="text-xs font-semibold text-indigo-600 hover:underline">
                                {t('View All')} →
                            </Link>
                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full text-left text-sm">
                                <thead>
                                    <tr className="text-xs font-semibold text-gray-500 border-b border-gray-200 pb-2">
                                        <th className="py-2.5 px-3">{t('Sale ID')}</th>
                                        <th className="py-2.5 px-3">{t('Customer')}</th>
                                        <th className="py-2.5 px-3 text-right">{t('Total TTC')}</th>
                                        <th className="py-2.5 px-3 text-center">{t('Status')}</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-gray-100">
                                    {salesFeed.map((sale) => (
                                        <tr key={sale.id} className="hover:bg-gray-50/80 transition-colors">
                                            <td className="py-3 px-3 font-semibold text-gray-900">
                                                #{sale.id}
                                            </td>
                                            <td className="py-3 px-3 text-gray-700">
                                                {sale.customer_name || 'Standard Customer'}
                                            </td>
                                            <td className="py-3 px-3 font-semibold text-indigo-600 text-right">
                                                {formatCurrency(sale.total_ttc)}
                                            </td>
                                            <td className="py-3 px-3 text-center">
                                                <span className={`px-2 py-0.5 rounded text-xs font-medium ${sale.status === 'Completed' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`}>
                                                    {sale.status}
                                                </span>
                                            </td>
                                        </tr>
                                    ))}
                                    {salesFeed.length === 0 && (
                                        <tr>
                                            <td colSpan="4" className="py-8 text-center text-gray-400 italic font-medium">{t('No transactions recorded today.')}</td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>

                {/* Right Sidebar: Loss Analysis */}
                <div className="space-y-6">
                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-base font-bold text-gray-800 mb-1">{t('Slow-Moving Inventory')}</h3>
                        <p className="text-xs text-gray-500 mb-4">{t('Inventory tying up working capital.')}</p>
                        
                        <div className="space-y-4">
                            <div className="p-4 rounded-lg bg-rose-50 border border-rose-100">
                                <p className="text-xs font-semibold text-rose-600 uppercase tracking-wider mb-1">{t('Capital in Slow Stock')}</p>
                                <p className="text-2xl font-bold text-rose-700">{formatCurrency(loss.total_slow_value)}</p>
                                <p className="text-xs text-rose-500 mt-1">{t('Estimated dormant value')}</p>
                            </div>

                            <div className="space-y-2">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider">{t('Top Dormant Items')}</h4>
                                {loss.slow_movers.slice(0, 5).map((item, i) => (
                                    <div key={i} className="flex items-center justify-between p-2.5 hover:bg-gray-50 rounded-lg transition-colors border border-gray-100">
                                        <div className="flex flex-col">
                                            <span className="text-sm font-semibold text-gray-800 truncate w-32">{item.name}</span>
                                            <span className="text-xs text-gray-400">{t('Stock')}: {item.stock}</span>
                                        </div>
                                        <span className="text-sm font-semibold text-rose-600">{formatCurrency(item.value)}</span>
                                    </div>
                                ))}
                            </div>

                            <div className="pt-3 border-t border-gray-100">
                                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">{t('Below Cost Sales')}</h4>
                                <p className="text-xl font-bold text-amber-600">{formatCurrency(loss.total_below_cost_loss)}</p>
                                <p className="text-xs text-gray-400">{t('Recorded in last 30 days')}</p>
                            </div>
                        </div>
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
                        <h3 className="text-base font-bold text-gray-800 mb-4">{t('Top Contributing Products')}</h3>
                        <div className="space-y-3">
                            {data.top_contributors.map((item, i) => (
                                <div key={i} className="flex items-center gap-3">
                                    <div className="w-6 h-6 rounded-md bg-gray-100 flex items-center justify-center text-xs font-bold text-gray-500">
                                        {i + 1}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="text-xs font-semibold text-gray-800 truncate">{item.name}</p>
                                        <div className="h-1.5 bg-gray-100 rounded-full mt-1 overflow-hidden">
                                            <div className="h-full bg-indigo-600 rounded-full" style={{ width: `${(item.profit_contribution / (data.top_contributors[0]?.profit_contribution || 1)) * 100}%` }}></div>
                                        </div>
                                    </div>
                                    <p className="text-xs font-semibold text-indigo-600 whitespace-nowrap">{formatCurrency(item.profit_contribution)}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

const SummaryCard = ({ title, value, subtitle, icon, trend, trendUp, highlight }) => (
    <div className={`rounded-xl p-5 border shadow-sm transition-all ${
        highlight 
            ? 'bg-indigo-600 text-white border-indigo-700' 
            : 'bg-white text-gray-900 border-gray-200'
    }`}>
        <div className="flex justify-between items-start mb-3">
            <span className="text-xl">{icon}</span>
            {trend && (
                <span className={`px-2 py-0.5 rounded text-xs font-semibold ${
                    highlight 
                        ? 'bg-indigo-700 text-indigo-100' 
                        : trendUp ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-rose-50 text-rose-700 border border-rose-200'
                }`}>
                    {trendUp ? '↑' : '↓'} {trend}
                </span>
            )}
        </div>
        <h3 className={`text-xs font-semibold uppercase tracking-wider mb-1 ${highlight ? 'text-indigo-200' : 'text-gray-500'}`}>{title}</h3>
        <p className="text-2xl font-bold tracking-tight">{value}</p>
        <p className={`text-xs mt-1 font-medium ${highlight ? 'text-indigo-200' : 'text-gray-400'}`}>{subtitle}</p>
    </div>
);

export default BusinessAnalysis;
